import { createMemo, createRoot, createSignal } from "solid-js"

/**
 * Root attribute that mirrors `mobileLayout()`. CSS and the Tailwind `md:`
 * variant read it so styling and the JS shell choose the same layout.
 */
const MOBILE_LAYOUT_ATTRIBUTE = "data-mobile-layout"

const DESKTOP_MIN_WIDTH = 768
// Android's sw600dp phone/tablet boundary. A phone's short side stays below it
// in either orientation, so landscape phones keep the mobile shell.
const TABLET_MIN_SHORT_SIDE = 600
const TOUCH_PRIMARY_QUERY = "(hover: none) and (pointer: coarse)"

export type LayoutViewportInput = {
  width: number
  touchPrimary: boolean
  screenShortSide: number
}

export function isMobileLayout(input: LayoutViewportInput) {
  return input.width < DESKTOP_MIN_WIDTH || (input.touchPrimary && input.screenShortSide < TABLET_MIN_SHORT_SIDE)
}

// The screen, unlike the window, does not shrink when a keyboard resizes the
// layout viewport, so opening a keyboard cannot flip a tablet into the phone shell.
const screenShortSide = () => Math.min(window.screen.width, window.screen.height)

type LayoutViewportState = {
  width: () => number
  mobile: () => boolean
  dispose: () => void
}

let state: LayoutViewportState | undefined

/**
 * Starts the app-lifetime owner of the layout decision and applies the root
 * attribute synchronously, so the first paint already uses the right layout.
 */
export function installLayoutViewport(): LayoutViewportState {
  if (state) return state
  if (typeof window === "undefined") {
    return { width: () => 1200, mobile: () => false, dispose: () => {} }
  }
  state = createRoot((dispose) => {
    const media = window.matchMedia(TOUCH_PRIMARY_QUERY)
    const [width, setWidth] = createSignal(window.innerWidth)
    const [touchPrimary, setTouchPrimary] = createSignal(media.matches)
    const [shortSide, setShortSide] = createSignal(screenShortSide())
    const mobile = createMemo(() =>
      isMobileLayout({ width: width(), touchPrimary: touchPrimary(), screenShortSide: shortSide() }),
    )
    const apply = () => document.documentElement.toggleAttribute(MOBILE_LAYOUT_ATTRIBUTE, mobile())
    apply()

    let frame: number | undefined
    const resize = () => {
      if (frame !== undefined) return
      frame = window.requestAnimationFrame(() => {
        frame = undefined
        setWidth(window.innerWidth)
        setShortSide(screenShortSide())
        apply()
      })
    }
    const pointerChange = () => {
      setTouchPrimary(media.matches)
      apply()
    }
    window.addEventListener("resize", resize, { passive: true })
    media.addEventListener("change", pointerChange)

    return {
      width,
      mobile,
      dispose() {
        window.removeEventListener("resize", resize)
        media.removeEventListener("change", pointerChange)
        if (frame !== undefined) window.cancelAnimationFrame(frame)
        document.documentElement.removeAttribute(MOBILE_LAYOUT_ATTRIBUTE)
        state = undefined
        dispose()
      },
    }
  })
  return state
}

export function createLayoutViewport() {
  const viewport = installLayoutViewport()
  const compact = createMemo(() => viewport.width() < DESKTOP_MIN_WIDTH)
  return { width: viewport.width, compact, mobile: viewport.mobile }
}
